import type { TranslationTable, Widen } from "./types/translation-table";

export const SUPPORTED_LOCALES = [
  "en_GB",
  "fr_FR",
  "es_ES",
  "pt_BR",
  "de_DE",
  "it_IT",
  "ar",
  "ja_JP",
  "hi_IN",
  "zh_CN",
  "zh_TW",
  "nl_NL",
  "nb_NO",
  "sv_SE",
  "fi_FI",
  "da_DK",
  "is_IS",
] as const;

export type LocaleCode = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: LocaleCode = "en_GB";

/**
 * QL-17 — the request header the advisor flow wizard sends with the flow's
 * language, so /internal errors come back in that language instead of the
 * advisor's own.
 */
export const FLOW_LANGUAGE_HEADER = "x-flow-language";

/**
 * QL-17 — the one way to read a translation table (web: `useT`, which calls
 * this with the current language). Returns the table for `lang`, or en_GB
 * when the table has no such locale. Function values (interpolation) come
 * back as they are.
 */
export function pickT<E extends object>(table: TranslationTable<E>, lang: string): Widen<E> {
  const tables: Partial<Record<string, unknown>> = table;
  return (tables[lang] ?? table.en_GB) as Widen<E>;
}

/**
 * The first supported locale among `candidates`, else en_GB. Pass them in
 * priority order, e.g. `resolveLocale(user.language, tenant.defaultLanguage)`;
 * an empty or unknown value is skipped.
 */
export function resolveLocale(...candidates: (string | null | undefined)[]): LocaleCode {
  for (const candidate of candidates) {
    if (candidate && (SUPPORTED_LOCALES as readonly string[]).includes(candidate)) {
      return candidate as LocaleCode;
    }
  }
  return DEFAULT_LOCALE;
}

/**
 * Locales a tenant can actually enable today (QL-6). The rest of
 * SUPPORTED_LOCALES stay visible in the settings UI but are disabled with an
 * "Additional languages are coming soon" note, and the tenant settings
 * service rejects them as default or supported languages.
 */
export const ENABLED_LOCALES = ["en_GB", "nb_NO"] as const satisfies readonly LocaleCode[];

export type EnabledLocaleCode = (typeof ENABLED_LOCALES)[number];

export function isLocaleEnabled(locale: string): locale is EnabledLocaleCode {
  return (ENABLED_LOCALES as readonly string[]).includes(locale);
}

export const LOCALE_NAMES: Record<LocaleCode, string> = {
  en_GB: "English",
  fr_FR: "French",
  es_ES: "Spanish",
  pt_BR: "Portuguese (Brazil)",
  de_DE: "German",
  it_IT: "Italian",
  ar: "Arabic",
  ja_JP: "Japanese",
  hi_IN: "Hindi",
  zh_CN: "Chinese (Simplified)",
  zh_TW: "Chinese (Traditional)",
  nl_NL: "Dutch",
  nb_NO: "Norwegian",
  sv_SE: "Swedish",
  fi_FI: "Finnish",
  da_DK: "Danish",
  is_IS: "Icelandic",
};

export const LOCALE_NATIVE_NAMES: Record<LocaleCode, string> = {
  en_GB: "English",
  fr_FR: "Français",
  es_ES: "Español",
  pt_BR: "Português",
  de_DE: "Deutsch",
  it_IT: "Italiano",
  ar: "العربية",
  ja_JP: "日本語",
  hi_IN: "हिन्दी",
  zh_CN: "简体中文",
  zh_TW: "繁體中文",
  nl_NL: "Nederlands",
  nb_NO: "Norsk",
  sv_SE: "Svenska",
  fi_FI: "Suomi",
  da_DK: "Dansk",
  is_IS: "Íslenska",
};

export type LocalizedString = Partial<Record<LocaleCode, string>>;
export type LocalizedStringArray = Partial<Record<LocaleCode, string[]>>;

export function isRTL(locale: LocaleCode): boolean {
  return locale === "ar";
}

/**
 * The locales to try, in order: `lang`, then `fallbackLang` (pass the
 * tenant's default language for tenant content), then en_GB, then every
 * supported locale.
 */
function lookupOrder(lang: string, fallbackLang: string): string[] {
  return [...new Set<string>([lang, fallbackLang, DEFAULT_LOCALE, ...SUPPORTED_LOCALES])];
}

/**
 * QL-17 — the one reader for tenant-edited text (`nameI18n` etc.). A plain
 * string comes back as it is. A localized string gives the first non-empty
 * value in `lang` → `fallbackLang` → en_GB → SUPPORTED_LOCALES order, else "".
 * Web components use `useLocalize()`, which passes the tenant default.
 */
export function getLocalizedValue(
  value: string | LocalizedString | null | undefined,
  lang: string,
  fallbackLang: string = DEFAULT_LOCALE,
): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  const values: Partial<Record<string, string>> = value;
  for (const locale of lookupOrder(lang, fallbackLang)) {
    const text = values[locale];
    if (text) return text;
  }
  return "";
}

/** `getLocalizedValue` for lists (e.g. dropdown options); an empty list counts as missing. */
export function getLocalizedArray(
  value: LocalizedStringArray | null | undefined,
  lang: string,
  fallbackLang: string = DEFAULT_LOCALE,
): string[] {
  if (!value) return [];
  const values: Partial<Record<string, string[]>> = value;
  for (const locale of lookupOrder(lang, fallbackLang)) {
    const list = values[locale];
    if (list && list.length > 0) return list;
  }
  return [];
}

/**
 * Build a localized string holding the same value for the user's language plus
 * every tenant-supported language — storing only the creator's language leaves
 * other locales blank.
 */
export function buildI18n(
  value: string,
  userLanguage: string,
  tenantSupportedLanguages: readonly string[],
): LocalizedString {
  return Object.fromEntries(
    [...new Set([userLanguage, ...tenantSupportedLanguages])].map((lang) => [lang, value]),
  );
}

/**
 * Rename a localized string from the user's language. The new value also
 * replaces every language that is blank or still holds the name being replaced
 * (the untranslated copies `buildI18n` wrote on create), so those locales do
 * not keep the old name. Languages with their own translation are left alone.
 *
 * The name being replaced is the one stored for the user's language, never a
 * fallback: when that language is blank, the name the user saw belongs to
 * another language and is that language's real translation.
 */
export function renameI18n(
  existing: LocalizedString | null | undefined,
  value: string,
  userLanguage: string,
  tenantSupportedLanguages: readonly string[],
): LocalizedString {
  const current: Partial<Record<string, string>> = existing ?? {};
  const previous = current[userLanguage];
  const languages = new Set([userLanguage, ...tenantSupportedLanguages, ...Object.keys(current)]);

  return Object.fromEntries(
    [...languages].map((lang) => {
      const stored = current[lang];
      const isUntranslated = !stored || stored === previous;
      return [lang, lang === userLanguage || isUntranslated ? value : stored];
    }),
  );
}
